import React, { useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import CheckInModal from '../member/CheckInModal';

const client = generateClient({ authMode: 'userPool' });

interface Props { eventId: string; onCheckedIn: () => void; }

const MIN_QUERY = 2;
const MAX_RESULTS = 25;

/**
 * #161: match a member against the typed query. Exact email or member number
 * first, then alphabetically. Exported for tests.
 */
export function matchMembers(all: any[], query: string): any[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < MIN_QUERY) return [];
  const hits = all.filter((m: any) => {
    const first = (m.firstName || '').toLowerCase();
    const last = (m.lastName || '').toLowerCase();
    return (
      (m.email || '').toLowerCase() === needle ||
      (m.memberNumber || '').toLowerCase() === needle ||
      first.includes(needle) ||
      last.includes(needle) ||
      `${first} ${last}`.includes(needle)
    );
  });
  const exact = (m: any) =>
    (m.email || '').toLowerCase() === needle || (m.memberNumber || '').toLowerCase() === needle ? 0 : 1;
  hits.sort((a: any, b: any) =>
    exact(a) - exact(b) || `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
  );
  return hits;
}

const AdminCheckInPanel: React.FC<Props> = ({ eventId, onCheckedIn }) => {
  const [search, setSearch] = useState('');
  // #161: the member list is loaded once (all pages) and then filtered as the
  // admin types. The Search button reloads it fresh.
  const [members, setMembers] = useState<any[] | null>(null);
  const [target, setTarget] = useState<any | null>(null);
  const [family, setFamily] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const loading = useRef<Promise<any[] | null> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadMembers = async (fresh = false): Promise<any[] | null> => {
    if (!fresh && members) return members;
    if (!fresh && loading.current) return loading.current;
    setBusy(true);
    const p = (async () => {
      try {
        /*
          Load every member once, following nextToken, then match locally.

          This was two list() calls with a filter and limit: 5 / limit: 10, which
          is the DynamoDB filter-plus-limit trap — the same defect fixed in
          CompetitionDayDashboard on 20 Sep 2026. AppSync runs a Scan, reads ONE
          page of the requested size, and applies the FilterExpression AFTER that
          page is read. So `limit: 5` against a 250-member table read five
          arbitrary rows and asked whether any of them matched.

          Matching is done client-side because the previous server-side `contains`
          was also case-sensitive — "chisholm" would not find "Chisholm".
        */
        let all: any[] = [];
        let nextToken: string | null = null;
        do {
          const page: any = await (client.models as any).Member.list({
            limit: 1000,
            ...(nextToken ? { nextToken } : {}),
          });
          // #132: one malformed Member record (e.g. no firstName) comes back as
          // null with a GraphQL error; keep the good records and log the bad ones.
          if (page?.errors?.length) {
            console.warn('AdminCheckInPanel: skipped malformed member records', page.errors.map((e: any) => e?.path?.join('.')));
          }
          all = all.concat((page?.data || []).filter((m: any) => m && typeof m === 'object'));
          nextToken = page?.nextToken ?? null;
        } while (nextToken);
        setMembers(all);
        setLoadFailed(false);
        return all;
      } catch (err) {
        console.error('AdminCheckInPanel search failed:', err);
        setLoadFailed(true);
        return null;
      } finally {
        setBusy(false);
        loading.current = null;
      }
    })();
    loading.current = p;
    return p;
  };

  const results = useMemo(() => (members ? matchMembers(members, search).slice(0, MAX_RESULTS) : []), [members, search]);
  const q = search.trim();
  const searchError = loadFailed
    ? 'Member search failed. Please try again.'
    : members && q.length >= MIN_QUERY && results.length === 0
      ? `No member found for "${q}".`
      : null;

  const onType = (value: string) => {
    setSearch(value);
    if (value.trim().length >= MIN_QUERY && !members) void loadMembers();
  };

  const runSearch = async () => {
    if (!search.trim()) return;
    await loadMembers(true);
  };

  const clear = () => {
    setSearch('');
    setLoadFailed(false);
    inputRef.current?.focus();
  };

  // #161: Enter never checks anyone in. With exactly one match it opens that
  // member's check-in dialog (which has its own confirm); otherwise it
  // (re)runs the search. Escape clears.
  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      clear();
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!q) return;
    const list = members ?? (await loadMembers());
    const hits = list ? matchMembers(list, search) : [];
    if (hits.length === 1) void openModalFor(hits[0]);
    else if (members) void loadMembers(true);
  };

  const openModalFor = async (m: any) => {
    setTarget(m);
    // Fetch family siblings if any
    if (m.familyGroupId) {
      const { data } = await (client.models as any).Member.membersByFamilyGroup({ familyGroupId: m.familyGroupId });
      setFamily((data || []).filter((x: any) => x.id !== m.id));
    } else {
      setFamily([]);
    }
    setOpen(true);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <h3 className="font-semibold text-gray-900 mb-2">Check in on behalf of a member</h3>
      <div className="flex gap-2">
        <label htmlFor="admin-checkin-search" className="sr-only">Search members to check in</label>
        <input
          id="admin-checkin-search"
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search by email, TFC-xxxx, or name"
          autoComplete="off"
          className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-300 rounded text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear"
            className="min-h-[44px] min-w-[44px] px-3 border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
          >
            ✕
          </button>
        )}
        <button type="button" onClick={runSearch} disabled={busy} className="min-h-[44px] px-4 py-2 bg-fishing-blue text-white rounded text-sm font-medium disabled:bg-gray-300">
          {busy ? 'Searching…' : 'Search'}
        </button>
      </div>
      {q.length > 0 && q.length < MIN_QUERY && (
        <p className="mt-2 text-xs text-gray-500">Type at least {MIN_QUERY} characters.</p>
      )}

      {searchError && (
        <p role="status" className="mt-3 text-sm text-gray-600">{searchError}</p>
      )}

      {results.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 border rounded">
          {results.map((m) => (
            <li key={m.id} className="p-2 flex items-center justify-between text-sm">
              <span>
                <strong>{m.firstName} {m.lastName}</strong>
                <span className="text-gray-500 ml-2">{m.memberNumber || '—'} · {m.email}</span>
              </span>
              <button onClick={() => openModalFor(m)} className="text-fishing-blue text-xs font-semibold hover:underline">
                Check in →
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && target && (
        <CheckInModal
          eventId={eventId}
          member={target}
          familyMembers={family}
          onClose={() => { setOpen(false); setTarget(null); }}
          onSuccess={() => { setOpen(false); setTarget(null); setSearch(''); onCheckedIn(); }}
        />
      )}
    </div>
  );
};

export default AdminCheckInPanel;
